<template>
	<SectionCard title="Verifica in due passaggi" icon="mdi-shield-key-outline">
		<template #actions>
			<v-chip v-if="status.enabled" color="success" label x-small text-color="white">Attiva</v-chip>
			<v-chip v-else label x-small outlined>Non attiva</v-chip>
		</template>
		<div>
			<p class="tf-hint mb-4">Al login, oltre alla password, viene chiesto un codice dall'app di autenticazione o via email.</p>

			<v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

			<!-- codici di backup appena generati: mostrati una volta sola -->
			<div v-else-if="backupCodes.length">
				<v-alert type="warning" dense text>
					Salva questi codici di backup in un posto sicuro. Ognuno vale una sola volta e servono per entrare se perdi il telefono o l'accesso all'email. Non verranno più mostrati.
				</v-alert>
				<div class="tf-codes mb-3">
					<code v-for="code in backupCodes" :key="code" class="tf-code">{{ code }}</code>
				</div>
				<v-btn outlined color="primary" class="mr-2" @click="copyBackupCodes">
					<v-icon left>mdi-content-copy</v-icon>
					Copia
				</v-btn>
				<v-btn outlined color="primary" class="mr-2" @click="downloadBackupCodes">
					<v-icon left>mdi-download</v-icon>
					Scarica
				</v-btn>
				<v-btn color="primary" @click="backupCodes = []">Ho salvato i codici</v-btn>
			</div>

			<!-- 2FA attiva -->
			<div v-else-if="status.enabled">
				<div class="tf-facts mb-4">
					<div class="tf-fact">
						<v-icon small color="primary" class="mr-2">{{ status.method === 'totp' ? 'mdi-cellphone-key' : 'mdi-email-lock-outline' }}</v-icon>
						<span class="tf-fact-label">Metodo</span>
						<strong>{{ status.method === 'totp' ? 'App di autenticazione' : 'Email' }}</strong>
					</div>
					<div class="tf-fact">
						<v-icon small color="primary" class="mr-2">mdi-key-chain-variant</v-icon>
						<span class="tf-fact-label">Codici di backup rimasti</span>
						<strong :class="{ 'error--text': status.backupCodesLeft <= 2 }">{{ status.backupCodesLeft }}</strong>
					</div>
				</div>
				<v-btn outlined color="primary" class="mr-2 mb-2" @click="openConfirm('backup')">
					<v-icon left>mdi-refresh</v-icon>
					Rigenera codici di backup
				</v-btn>
				<v-btn outlined color="error" class="mb-2" @click="openConfirm('disable')">
					<v-icon left>mdi-shield-off-outline</v-icon>
					Disattiva
				</v-btn>
			</div>

			<!-- configurazione in corso -->
			<div v-else-if="setup">
				<template v-if="setup.method === 'totp'">
					<p>1. Scansiona il QR con Google Authenticator (o Authy, Microsoft Authenticator).</p>
					<div class="tf-qr my-2"><img :src="setup.qrCode" alt="QR code per l'app di autenticazione" width="180" height="180" /></div>
					<p class="text-caption">
						Non riesci a scansionarlo? Inserisci a mano la chiave: <code>{{ setup.secret }}</code>
					</p>
					<p>2. Inserisci il codice di 6 cifre mostrato dall'app.</p>
				</template>
				<template v-else>
					<p>
						Abbiamo inviato un codice di 6 cifre a <strong>{{ setup.email }}</strong>. Inseriscilo per attivare la verifica via email.
					</p>
				</template>
				<v-text-field hide-details="auto"
					v-model="setupCode"
					label="Codice di verifica"
					outlined
					dense
					class="my-3"
					inputmode="numeric"
					autocomplete="one-time-code"
					style="max-width: 240px"
					@keyup.enter="enable"
				></v-text-field>
				<v-btn text class="mr-2" @click="setup = null">Annulla</v-btn>
				<v-btn v-if="setup.method === 'email'" text color="primary" class="mr-2" :loading="working" @click="start('email')">Invia di nuovo</v-btn>
				<v-btn color="primary" :loading="working" :disabled="!setupCode" @click="enable">Attiva</v-btn>
			</div>

			<!-- 2FA non attiva: scelta del metodo -->
			<div v-else>
				<v-btn color="primary" class="mr-2 mb-2" :loading="working" @click="start('totp')">
					<v-icon left>mdi-cellphone-key</v-icon>
					Usa un'app di autenticazione
				</v-btn>
				<v-btn color="primary" class="mb-2" :loading="working" @click="start('email')">
					<v-icon left>mdi-email-lock-outline</v-icon>
					Usa l'email
				</v-btn>
			</div>
		</div>

		<!-- conferma per disattivare o rigenerare: password + codice -->
		<v-dialog v-model="confirm.open" max-width="440">
			<v-card>
				<v-card-title class="text-h6">{{ confirm.action === 'disable' ? 'Disattiva la verifica in due passaggi' : 'Rigenera i codici di backup' }}</v-card-title>
				<v-card-text>
					<v-text-field hide-details="auto" v-model="confirm.password" label="Password attuale" type="password" outlined dense class="mb-3"></v-text-field>
					<v-text-field hide-details="auto" v-model="confirm.code" label="Codice di verifica o di backup" autocomplete="one-time-code" outlined dense></v-text-field>
					<v-btn v-if="status.method === 'email'" text small color="primary" class="px-0" :loading="sendingCode" @click="sendCode">Inviami il codice via email</v-btn>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="confirm.open = false">Annulla</v-btn>
					<v-btn :color="confirm.action === 'disable' ? 'error' : 'primary'" :loading="working" :disabled="!confirm.password || !confirm.code" @click="submitConfirm">Conferma</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</SectionCard>
</template>

<script>
/**
 * Autenticazione a due fattori dal Profilo del BO: attivazione (app TOTP o email), codici di backup,
 * rigenerazione e disattivazione. Messaggi verso la pagina con l'evento `notify` ({ color, text }).
 */
import SectionCard from '@/components/core/SectionCard.vue';

export default {
	name: 'TwoFactorCard',
	components: { SectionCard },
	data() {
		return {
			loading: true,
			working: false,
			sendingCode: false,
			status: { enabled: false, method: null, backupCodesLeft: 0 },
			setup: null,
			setupCode: '',
			backupCodes: [],
			confirm: { open: false, action: '', password: '', code: '' },
		};
	},
	async created() {
		await this.loadStatus();
	},
	methods: {
		async loadStatus() {
			this.loading = true;
			const result = await this.$store.dispatch('profile/twoFactorStatus');
			this.loading = false;
			if (result.success) {
				this.status = result.data.data;
			}
		},
		notify(color, text) {
			this.$emit('notify', { color, text });
		},
		errorText(code) {
			return this.$vuetify.lang.t(`$vuetify.errors.${code || 1007}`);
		},
		async start(method) {
			this.working = true;
			const result = await this.$store.dispatch('profile/twoFactorSetup', method);
			this.working = false;
			if (!result.success) {
				this.notify('error', this.errorText(result.message));
				return;
			}
			this.setup = result.data.data;
			this.setupCode = '';
		},
		async enable() {
			if (!this.setupCode) {
				return;
			}
			this.working = true;
			const result = await this.$store.dispatch('profile/twoFactorEnable', this.setupCode);
			this.working = false;
			if (!result.success) {
				this.notify('error', this.errorText(result.message));
				return;
			}
			this.setup = null;
			this.backupCodes = result.data.data.backupCodes;
			await this.loadStatus();
			this.notify('success', 'Verifica in due passaggi attivata. Gli altri dispositivi sono stati disconnessi');
		},
		openConfirm(action) {
			this.confirm = { open: true, action, password: '', code: '' };
		},
		async sendCode() {
			this.sendingCode = true;
			const result = await this.$store.dispatch('profile/twoFactorSendCode');
			this.sendingCode = false;
			this.notify(result.success ? 'success' : 'error', result.success ? 'Codice inviato via email' : this.errorText(result.message));
		},
		async submitConfirm() {
			const payload = { password: this.confirm.password, code: this.confirm.code };
			this.working = true;
			const result = await this.$store.dispatch(this.confirm.action === 'disable' ? 'profile/twoFactorDisable' : 'profile/twoFactorBackupCodes', payload);
			this.working = false;
			if (!result.success) {
				this.notify('error', this.errorText(result.message));
				return;
			}
			if (this.confirm.action === 'backup') {
				this.backupCodes = result.data.data.backupCodes;
			} else {
				this.notify('success', 'Verifica in due passaggi disattivata');
			}
			this.confirm.open = false;
			await this.loadStatus();
		},
		copyBackupCodes() {
			navigator.clipboard.writeText(this.backupCodes.join('\n')).then(
				() => this.notify('success', 'Codici copiati'),
				() => this.notify('error', 'Copia non riuscita'),
			);
		},
		downloadBackupCodes() {
			const blob = new Blob([`Codici di backup AZ Seller\n\n${this.backupCodes.join('\n')}\n`], { type: 'text/plain' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'azseller-codici-backup.txt';
			link.click();
			URL.revokeObjectURL(url);
		},
	},
};
</script>

<style scoped>
.tf-hint {
	font-size: 12px;
	color: #5f6b7a;
}
.tf-facts {
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.tf-fact {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 8px 12px;
	font-size: 14px;
}
.tf-fact + .tf-fact {
	border-top: 1px solid #eef2f7;
}
.tf-fact-label {
	flex: 1 1 auto;
	color: #5f6b7a;
}
.tf-codes {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
	gap: 6px;
}
.tf-code {
	text-align: center;
	font-size: 14px;
	padding: 6px 8px;
	background: #f3f6fa;
	border: 1px solid #e1e7ef;
	border-radius: 6px;
}
.tf-qr {
	display: inline-block;
	padding: 8px;
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
}
</style>
